// src/utils/formatters.js

/**
 * Форматирование длительности в минутах
 * @param {number} mins - минуты
 * @returns {string} - "Xч Yмин"
 */
export function formatDuration(mins) {
	const m = Number(mins) || 0
	const h = Math.floor(m / 60)
	const mm = Math.floor(m % 60)
	return `${h}ч ${mm}мин`
}

/**
 * Получение инициалов из имени
 * @param {string} name - полное имя
 * @returns {string} - инициалы (макс 2 буквы)
 */
export function getInitials(name) {
	if (!name) return '?'
	return name
		.split(' ')
		.map(n => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)
}

/**
 * Форматирование рейтинга
 * @param {number|string} val - значение рейтинга
 * @returns {string} - форматированный рейтинг
 */
export function formatRating(val) {
	const n = Number(String(val ?? '').replace(',', '.')) || 0
	return n.toLocaleString('ru-RU', {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	})
}

/**
 * Склонение слова "модуль"
 * @param {number} val - количество
 * @returns {string}
 */
export function pluralizeModule(val) {
	const n = Number(val) || 0
	const mod10 = n % 10
	const mod100 = n % 100

	if (mod10 === 1 && mod100 !== 11) return 'модуль'
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'модуля'
	return 'модулей'
}

/**
 * Склонение слова "конференция"
 * @param {number} n - количество
 * @returns {string}
 */
export function pluralizeConference(n) {
	const abs = Math.abs(n) % 100
	const n1 = abs % 10
	if (abs > 10 && abs < 20) return 'конференций'
	if (n1 > 1 && n1 < 5) return 'конференции'
	if (n1 === 1) return 'конференция'
	return 'конференций'
}

/**
 * Склонение слова "урок"
 * @param {number} n - количество
 * @returns {string}
 */
export function pluralizeLesson(n) {
	const abs = Math.abs(n) % 100
	const n1 = abs % 10
	if (abs > 10 && abs < 20) return 'уроков'
	if (n1 > 1 && n1 < 5) return 'урока'
	if (n1 === 1) return 'урок'
	return 'уроков'
}

/**
 * Форматирование даты в короткий формат
 * @param {string|Date} dateStr - дата
 * @returns {string}
 */
export function formatDateShort(dateStr) {
	if (!dateStr) return ''
	const date = new Date(dateStr)
	const today = new Date()
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)

	if (date.toDateString() === today.toDateString()) return 'Сегодня'
	if (date.toDateString() === tomorrow.toDateString()) return 'Завтра'

	return date.toLocaleDateString('ru-RU', {
		day: 'numeric',
		month: 'short',
		weekday: 'short',
	})
}

/**
 * Форматирование времени
 * @param {string|Date} dateStr - дата
 * @returns {string} - "HH:MM"
 */
export function formatTime(dateStr) {
	if (!dateStr) return '--:--'
	return new Date(dateStr).toLocaleTimeString('ru-RU', {
		hour: '2-digit',
		minute: '2-digit',
	})
}

/**
 * Debounce функция
 * @param {Function} func - функция для debounce
 * @param {number} wait - задержка в мс
 * @returns {Function}
 */
export function debounce(func, wait) {
	let timeout
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout)
			func(...args)
		}
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
	}
}
